import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Target, Zap, Star, ArrowRight, Users, X, ClipboardCheck, Home, PlayCircle } from 'lucide-react';
import { type ModuleData, type ModuleId } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useActiveAssignments } from '../../app/hooks/useActiveAssignments';
import { SESSION_CONFIG } from '../../config/constants';
import { DjayIdentity, useDjaySelection } from '../../mascot';
import type { DjayEmotion } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { PracticeAssignmentBanner } from './PracticeAssignmentBanner';
import { Banner } from '../ui/Banner';
import { PRIMARY } from '../../config/designTokens';
import type { AssessmentConfig, PracticeAssignment } from '../../assessment';
import { soundManager } from '../../utils/soundManager';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import { getSmartSessions } from '../../mastery';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { getTimeContext } from '../../utils/timeContext';
import { DailyChallengeCard } from './DailyChallengeCard';
import { GlanceBar } from './GlanceBar';
import { SmartSessionCard } from './SmartSessionCard';
import { useHomeBanners } from './useHomeBanners';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
  onStartHomeworkPractice: (assignment: PracticeAssignment) => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab,
  onStartHomeworkPractice
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant,
    selectedAccessory,
    selectedTier,
    selectedTreatment
  } = useDjaySelection();
  // Active assignments — single source of truth via shared hook
  // (audit P1-3). We further filter the assessments by the currently
  // selected group below; practice assignments are not group-scoped.
  const {
    assessments: allAssessments,
    practiceAssignments: activePracticeAssignments
  } = useActiveAssignments(activeAccount?.id);
  const activeAssignments = useMemo(() => allAssessments.filter(a => a.group === selectedGroup), [allAssessments, selectedGroup]);
  // Learner-home preference reads (firstFocus CTA + klascode banner) are
  // owned by a dedicated hook (App-Feel 2027 W21 / V-AF-04) so this page
  // never touches `dataLayer` directly.
  const {
    firstFocusModuleId,
    clearFirstFocus,
    showKlascodeBanner,
    dismissKlascodeBanner
  } = useHomeBanners(activeAccount, accounts, modules);
  // Time-context (audit LEARNER_HOME_FLOW_UX §P2-5, P2-6) — single source
  // of truth for greeting + Djay sleepy state. Replaces the previous
  // ad-hoc `hour < 8 || hour < 12 → 'Goedemorgen'` block which had a
  // dode tak and lacked a 'Goedenacht' bucket entirely.
  const timeContext = useMemo(() => getTimeContext(), []);
  const greeting = timeContext.greeting;
  // Contextual Djay emotion for the hero (audit §P2-4). Previously this
  // was `getDjayGreeting(displayName).emotion` — a deterministic function
  // of the learner's NAME, so the same child saw the same emotion every
  // single visit regardless of streak, time, or progress. We now reflect
  // the learner's actual state, in priority order.
  const dailyGoalReached = (state.dailyStreak?.todayQuestionsAnswered ?? 0) >= (state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL);
  const heroDjayEmotion: DjayEmotion = useMemo(() => {
    const streakDays = state.dailyStreak?.currentWeekDays ?? 0;
    const startedAny = modules.some(m => getAnsweredQuestions(m.id).length > 0);
    if (dailyGoalReached) return 'proud';
    if (streakDays >= 5) return 'excited';
    if (streakDays === 0 && startedAny) return 'encouraging';
    if (timeContext.djaySleepy) return 'sleeping';
    return 'happy';
  }, [dailyGoalReached, state.dailyStreak?.currentWeekDays, timeContext.djaySleepy, modules, getAnsweredQuestions]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  const weeklyGoal = state.dailyStreak?.weeklyGoalTarget ?? 3;
  const level = state.stats.level;
  const xp = state.stats.xp;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  // F4.1 — three-segment Smart Sessions (replaces recommendation + continue + decay-tip)
  const smartSessions = useMemo(() => {
    return getSmartSessions(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Daily Challenge
  const dailyChallenge = useMemo(() => {
    return getTodayChallenge(state, modules, selectedGroup);
  }, [state, modules, selectedGroup]);
  const handleStartChallenge = () => {
    if (dailyChallenge.moduleId) {
      onStartPractice(dailyChallenge.moduleId);
    } else {
      onNavigateToModules();
    }
  };
  return <PageContainer data-id="element-1392" data-mp="i1bn5uof2d3j">
      {/* Skip-link (audit P2-7) — visually hidden until focused; lets
                             keyboard users jump past the GlanceBar/Hero/banners straight
                             to the primary action (SmartSessionCard / WelcomeCard). */}
      <a href="#main-action" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:font-bold" data-id="element-1393" data-mp="3dvwshau5h10p">
        
        Spring naar startactie
      </a>
      <GlanceBar streakDays={currentStreak} dailyProgress={todayQuestionsAnswered} dailyGoalTarget={dailyGoal} level={level} xp={xp} weeklyGoal={weeklyGoal} weeklyActivity={weeklyActivity} onNavigateToModules={onNavigateToModules} data-id="element-1394" data-mp="2z8btnt4b5nyl" />
      

      <motion.div variants={itemVariants} className="mb-6 sm:mb-8" data-id="element-1395" data-mp="2q5sp94kvj3a8">
        <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses={PRIMARY.gradient} djayEmotion={heroDjayEmotion} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} djayTreatment={selectedTreatment} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={[]} data-id="element-1396" data-mp="2l7hfetukwss0" />
        
      </motion.div>

      {/* FIRST-FOCUS BANNER moved (audit P1-4): it now sits directly above
                             the SmartSessionCard / WelcomeCard so the "klaar om te beginnen?"
                             framing is immediately above the Start-CTA. Old position above
                             assignments pushed the primary action below the fold. */}

      {/* KLASCODE BANNER — hidden when assignments are present to reduce clutter */}
      {showKlascodeBanner && activeAssignments.length === 0 && activePracticeAssignments.length === 0 && <motion.div variants={itemVariants} data-id="element-1397" data-mp="35qzqtrbxjrat">
            <Card className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden shadow-sm" data-id="element-1398" data-mp="35rrn74dcpt25">
              <div className="flex items-center gap-3" data-id="element-1399" data-mp="2vud4sz7vau77">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 shadow-sm" data-id="element-1400" data-mp="p1kozaxfrw6h">
                  <Users className="w-5 h-5" data-id="element-1401" data-mp="tytc7pxfyqel" />
                </div>
                <div data-id="element-1402" data-mp="2ejzpevrnykzg">
                  <h3 className="text-sm font-bold text-amber-900" data-id="element-1403" data-mp="2ej7xbux9z5dx">
                    Heb je een klascode?
                  </h3>
                  <p className="text-xs text-amber-700 font-medium" data-id="element-1404" data-mp="2o8rgl1owh19b">
                    Je kunt deze op elk moment invullen via Instellingen.
                  </p>
                </div>
              </div>
              <button onClick={dismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1405" data-mp="2x962a25s0idc">
            
                <X className="w-4 h-4" data-id="element-1406" data-mp="377digis8qwea" />
              </button>
            </Card>
          </motion.div>}

      {/* ACTIVE ASSIGNMENTS - Max 1 banner (Assessment > Practice) */}
      {activeAssignments.length > 0 ? <motion.div variants={itemVariants} className="mb-4" data-id="element-1407" data-mp="1ctcn47vwgriy">
          <Banner variant="assignment" icon={ClipboardCheck} title={`${activeAssignments.length} ${activeAssignments.length === 1 ? 'toets staat' : 'toetsen staan'} voor je klaar!`} action={<Button onClick={onNavigateToAssessment} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1409" data-mp="33dju7a5pefrx">
            
                Bekijk toetsen <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1410" data-mp="2teuadbu8yl91" />
              </Button>} data-id="element-1408" data-mp="1cvp006mswbp5">
          
            Je leraar heeft nieuwe toetsen klaargezet.
          </Banner>
        </motion.div> : activePracticeAssignments.length === 1 ? <motion.div key={activePracticeAssignments[0].id} variants={itemVariants} className="mb-4" data-id="element-1411" data-mp="3m1yivqsjthpk">
        
          <PracticeAssignmentBanner assignment={activePracticeAssignments[0]} teacherName="je leraar" onStartPractice={() => {
        const assignment = activePracticeAssignments[0];
        if (assignment.moduleIds.length > 0) {
          onStartHomeworkPractice(assignment);
        } else {
          onNavigateToModules();
        }
      }} data-id="element-1412" data-mp="3h45dv12zetqv" />
        
        </motion.div> : activePracticeAssignments.length > 1 ? <motion.div variants={itemVariants} className="mb-4" data-id="element-1413" data-mp="3uqxspresg8i">
          <Banner variant="assignment" icon={PlayCircle} title={`${activePracticeAssignments.length} huiswerkjes wachten op je!`} action={<Button onClick={() => {
        const first = activePracticeAssignments[0];
        if (first.moduleIds.length > 0) {
          onStartHomeworkPractice(first);
        } else {
          onNavigateToModules();
        }
      }} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1415" data-mp="1riya8v2onti6">
            
                Start eerste <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1416" data-mp="2agioz9bqhbp2" />
              </Button>} data-id="element-1414" data-mp="1mkmy2dox16ws">
          
            Je leraar heeft meerdere oefen-opdrachten klaargezet.
          </Banner>
        </motion.div> : null}

      {/* QUICK NAVIGATION — mobile only (audit P2-1).
                                 On desktop the Sidebar already lists these three routes permanently,
                                 so showing them again creates dead duplicate UI above the fold.
                                 On mobile the Sidebar lives behind the hamburger, so the trio still
                                 earns its place there. */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3 sm:gap-4 md:hidden" data-id="element-1417" data-mp="o8aggn74xaa6">
        
        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToModules();
      }} aria-label="Ga naar Mijn Werelden" className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all group shadow-sm" data-id="element-1418" data-mp="12x6owld3spbb">
          
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1419" data-mp="1lug944q966tm">
            <Target className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" data-id="element-1420" data-mp="1gtaaxrdtprcp" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1421" data-mp="3nm30i7e243qm">
            Mijn Werelden
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToLab();
      }} aria-label="Ga naar Oefenlab" className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-purple-200 hover:bg-purple-50 transition-all group shadow-sm" data-id="element-1422" data-mp="1y4z3umd9ymor">
          
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1423" data-mp="1tcxlqjtfe1qt">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" data-id="element-1424" data-mp="1kdu1vdmfpib9" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1425" data-mp="15ga0shsns7ad">
            Oefenlab
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToAssessment();
      }} aria-label="Ga naar Toetsen" className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-orange-200 hover:bg-orange-50 transition-all group shadow-sm" data-id="element-1426" data-mp="1ag64efxg1wpe">
          
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1427" data-mp="14osbkizrehku">
            <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" data-id="element-1428" data-mp="von6ifi1fv9u" />
            
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1429" data-mp="qqvgjr0yej8a">
            Toets
          </span>
        </button>
      </motion.div>

      {/* FIRST-FOCUS BANNER (audit P1-4) — eenmalig na onboarding.
                                 Staat NÉT boven SmartSessionCard zodat "klaar om met X te beginnen?"
                                 direct boven de start-CTA verschijnt i.p.v. boven assignments. */}
      {firstFocusModuleId && (() => {
      const focusModule = modules.find(m => m.id === firstFocusModuleId);
      if (!focusModule) return null;
      return <motion.div variants={itemVariants} data-id="element-1430" data-mp="3u8vfkgoj7e9r">
              <Card className="p-4 sm:p-5 border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-white rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm" data-id="element-1431" data-mp="3ua6kwkxmku2z">
                <div className="flex items-center gap-3" data-id="element-1432" data-mp="3u2cme6d9jlwg">
                  <div className="flex-shrink-0" data-id="element-1433" data-mp="3u2urlshwlagi">
                    <DjayIdentity size="sm" emotion="pointing" data-id="element-1434" data-mp="2wbkanba02my" />
                  </div>
                  <div data-id="element-1435" data-mp="31dntuvq9wowb">
                    <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-1436" data-mp="31e74drdlgb0b">
                      Klaar om met {focusModule.name} te beginnen?
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 font-medium" data-id="element-1437" data-mp="3bbv7p5p9mqu3">
                      Je hebt dit vak gekozen in de welkomstcheck — klik om te
                      starten.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-1438" data-mp="2imo0libqwsx8">
                  <Button onClick={() => {
              onStartPractice(firstFocusModuleId);
              void clearFirstFocus();
            }} size="sm" variant="primary" data-id="element-1439" data-mp="2nkps8rn8m0kb">
                  
                    Start <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1440" data-mp="2alp1qhbu1gay" />
                  </Button>
                  <button type="button" onClick={() => {
              void clearFirstFocus();
            }} aria-label="Verberg deze tip" className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-1441" data-mp="2pa34ytd5v6s1">
                  
                    <X className="w-4 h-4" data-id="element-1442" data-mp="1rnm81p8cav6t" />
                  </button>
                </div>
              </Card>
            </motion.div>;
    })()}

      {/* PRIMARY ACTION — single SmartSessionCard replaces Continue + Recommendation + Herhaal-tip.
                                 Fallback (audit P3-3): if the learner has started somewhere but the
                                 smart-session engine returns no segments today, render a soft
                                 "kies zelf" CTA instead of letting the surface go silent. */}
      <motion.div id="main-action" variants={itemVariants} className="flex flex-col gap-4 scroll-mt-4" data-id="element-1443" data-mp="3kj6qt322qs0t">
        
        {hasStarted ? smartSessions.review || smartSessions.almostExpert || smartSessions.fresh ? <SmartSessionCard sessions={smartSessions} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} onStart={onStartPractice} data-id="element-1444" data-mp="3bkuymwlk7m69" /> : <Card className="p-5 sm:p-6 border-2 border-slate-100 bg-white rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1445" data-mp="75lhzt36rsf1">
              <div className="flex items-center gap-3" data-id="element-1446" data-mp="c695w5w4bt51">
                <div className="flex-shrink-0" data-id="element-1447" data-mp="3uhmf6y9pvn6m">
                  <DjayIdentity size="sm" emotion="thinking" data-id="element-1448" data-mp="2elj4iaib09w" />
                </div>
                <div data-id="element-1449" data-mp="2ciil4bq9oqx">
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-1450" data-mp="q4fv6xn04kt3">
                    Geen tip voor vandaag — kies zelf!
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 font-medium" data-id="element-1451" data-mp="uw7bmfcg1a5i">
                    Loop je werelden door en pak op waar je zin in hebt.
                  </p>
                </div>
              </div>
              <Button onClick={onNavigateToModules} size="sm" variant="primary" className="w-full sm:w-auto" data-id="element-1452" data-mp="1bgokcqq7lb0">
            
                Naar werelden <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1453" data-mp="3nl9s9u2qjq01" />
              </Button>
            </Card> : <WelcomeCard onStart={onNavigateToModules} data-id="element-1454" data-mp="p2uhokwpbkx1" />}
      </motion.div>

      {/* DAILY CHALLENGE */}
      {hasStarted && <motion.div variants={itemVariants} data-id="element-1455" data-mp="k3rllpyp8p17">
          <DailyChallengeCard challenge={dailyChallenge} djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} onStart={handleStartChallenge} data-id="element-1456" data-mp="k4t0y6mqzxoq" />
        
        </motion.div>}
    </PageContainer>;
}
function WelcomeCard({
  onStart
}: {
  onStart: () => void;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-lg relative overflow-hidden rounded-[2rem]" data-id="element-1457" data-mp="2zwgqvi3cgpy1">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1458" data-mp="1afcw62m4hiyt" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1459" data-mp="1fdedoj3wha86">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1460" data-mp="1kb6haadwqks7">
          
          {/* Audit P3-2: single avatar node, responsive size via the
            sm-breakpoint hidden/block pair. Keeps the visual responsive
            jump but avoids rendering two avatars in the DOM at once. */}
          <span className="sm:hidden" data-id="element-1461" data-mp="2340lqadwmui6">
            <DjayIdentity size="md" emotion="celebrating" animate data-id="element-1462" data-mp="230w7slan0keb" />
          </span>
          <span className="hidden sm:inline-block" data-id="element-1463" data-mp="27zposnp4utj8">
            <DjayIdentity size="lg" emotion="celebrating" animate data-id="element-1464" data-mp="8phlyfkqz53u" />
          </span>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1465" data-mp="dou6gto5c039">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1466" data-mp="2hpq8h6el1iiw">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-lg rounded-2xl sm:h-14 sm:text-lg sm:px-8 hover:shadow-xl transition-shadow" data-id="element-1467" data-mp="2vjhjykbvjch9">
          
          <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1468" data-mp="35h5bz2i3i5vq" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}